import axios from 'axios'
import * as _ from 'lodash-es'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { loadUserAttributes } from '../../common/service/userAttributes.js'

/*
 * Record that a session began (#671 step 1), once per SIGN-IN rather than once per tab.
 *
 * The first version used a boolean in sessionStorage, which meant a second sign-in in the same tab
 * - sign out, sign back in, or a Microsoft round trip after a failed one - was never recorded, and
 * the dashboard showed nothing at all. `auth_time` is Cognito's timestamp for the authentication
 * event itself: identical for every page load of one session, different for a new sign-in.
 *
 * Fire and forget, but only marked as recorded if the write succeeded, so a transient failure gets
 * another chance on the next page load instead of silently losing the record.
 */
const recordSignIn = (attributes, authTime) => {
  try {
    if (typeof sessionStorage === 'undefined' || authTime === undefined || authTime === null) return

    const key = 'hourglass.signinRecordedFor'
    if (sessionStorage.getItem(key) === String(authTime)) return

    let provider
    try {
      provider = sessionStorage.getItem('hourglass.signInProvider') || undefined
    } catch (err) {
      provider = undefined
    }

    axios
      .post('/api/log/signin', { attributes, provider })
      .then(() => {
        try {
          sessionStorage.setItem(key, String(authTime))
        } catch (err) {
          // nothing to do; worst case it records the same sign-in twice
        }
      })
      .catch(() => {})
  } catch (err) {
    // never let measurement break the thing it measures
  }
}

const state = {
  user: undefined,
}

const mutations = {
  SET_USER(state, user) {
    state.user = user
  },
}

const actions = {
  async setUser({ commit }) {
    // v6's getCurrentUser() carries no attributes, so fetch them once here and keep them on the
    // user; the rest of the app then reads them synchronously, the way it did under v5. Each of
    // these calls throws UserUnAuthenticatedException until a fresh page load's token store
    // settles, so retry as a unit before committing - callers (the quote/resources components)
    // run before the store's user exists and would otherwise 403.
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const currentUser = await getCurrentUser()
        const session = await fetchAuthSession()
        const attributes = await loadUserAttributes()
        // `custom:customer` is the tenancy boundary (#668). The pre-token-generation trigger
        // overrides it from the inventory snapshot; the Cognito attribute itself is mutable
        // (Cognito refuses to make an existing schema attribute immutable), so trust the token
        // claim over fetchUserAttributes.
        const tokenCustomer =
          session.tokens && session.tokens.idToken && session.tokens.idToken.payload['custom:customer']
        if (tokenCustomer) {
          attributes['custom:customer'] = tokenCustomer
        }
        commit('SET_USER', { ...currentUser, session, attributes })
        // `auth_time` identifies the sign-in itself, so a new session records again.
        const authTime =
          session.tokens && session.tokens.idToken && session.tokens.idToken.payload
            ? session.tokens.idToken.payload.auth_time
            : undefined
        recordSignIn(attributes, authTime)
        return
      } catch (err) {
        if (attempt === 4) throw err
        await new Promise(resolve => setTimeout(resolve, 300))
      }
    }
  },
}

const getters = {
  getCurrentUser: state => {
    return state.user
  },
  hasRole: state => roleName => {
    return state.user && state.user.session
      ? _.includes(state.user.session.tokens.accessToken.payload['cognito:groups'], roleName)
      : false
  },
}

export default {
  namespaced: true,
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters,
}
